import React, { useEffect, useMemo, useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { getHomepageSectionML } from '../../services/api';

/**
 * Trusted by Leading Organizations — seamless marquee carousel.
 * Uses the classic "duplicate the list and translate -50%" technique so the
 * loop is perfectly infinite without JavaScript. Hover pauses the animation.
 */
const ClientsSectionML = () => {
  const { language } = useLanguage();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const normalizedLang = ['pt', 'en', 'fr', 'ar', 'zh'].includes(language)
        ? language
        : (language?.startsWith('pt') ? 'pt' : 'en');
      const result = await getHomepageSectionML('clients', normalizedLang);
      if (result.success) setContent(result.data);
      setLoading(false);
    };
    load();
  }, [language]);

  const logos = useMemo(() => (Array.isArray(content?.logos) ? content.logos : []), [content]);
  // Duplicate the list for a seamless infinite scroll. If we have very few
  // logos, triple them so the track is always wider than the viewport.
  const track = useMemo(() => {
    if (logos.length === 0) return [];
    const copies = logos.length < 6 ? 3 : 2;
    return Array.from({ length: copies }).flatMap(() => logos);
  }, [logos]);

  if (loading || !content) {
    return (
      <section className="bg-[#F8F9FA] py-20">
        <div className="container mx-auto px-6 text-center">
          <div className="animate-pulse text-[#6C757D]">Loading...</div>
        </div>
      </section>
    );
  }

  const hasLogos = logos.length > 0;
  // Animation duration scales with the number of real logos so speed stays
  // roughly consistent regardless of how many are configured.
  const duration = Math.max(20, logos.length * 5);

  return (
    <section className="bg-[#F8F9FA] py-20 overflow-hidden" data-testid="clients-section">
      <div className="container mx-auto px-6">
        {/* Header */}
        <div className="text-center max-w-2xl mx-auto mb-12">
          <h3 className="text-3xl md:text-4xl font-semibold text-[#2B2D42] mb-4">
            {content.heading}
          </h3>
          <p className="text-base text-[#6C757D]">
            {content.subheading}
          </p>
        </div>

        {hasLogos ? (
          <div
            className="relative group"
            data-testid="marquee-wrapper"
            style={{
              maskImage:
                'linear-gradient(to right, transparent, black 8%, black 92%, transparent)',
              WebkitMaskImage:
                'linear-gradient(to right, transparent, black 8%, black 92%, transparent)',
            }}
          >
            <div
              className="flex w-max gap-12 md:gap-20 items-center marquee-track"
              style={{ animation: `bmp-marquee ${duration}s linear infinite` }}
            >
              {track.map((logo, i) => {
                const src = logo.logo_base64 || logo.logo_url;
                const name = logo.name || `Client ${i + 1}`;
                const body = src ? (
                  <img
                    src={src}
                    alt={name}
                    loading="lazy"
                    className="max-h-16 md:max-h-20 w-auto object-contain transition-all duration-300 hover:scale-110"
                  />
                ) : (
                  <div className="bg-[#E9ECEF] rounded-lg px-6 py-4 text-center">
                    <span className="text-sm text-[#6C757D] font-medium">{name}</span>
                  </div>
                );
                const inner = logo.website_url ? (
                  <a
                    href={logo.website_url}
                    target="_blank"
                    rel="noreferrer"
                    title={name}
                    className="flex items-center justify-center h-20"
                  >
                    {body}
                  </a>
                ) : (
                  <div title={name} className="flex items-center justify-center h-20">
                    {body}
                  </div>
                );
                return (
                  <div
                    key={`${i}-${logo.name}`}
                    className="flex-shrink-0 w-40 md:w-48"
                    data-testid={i < logos.length ? `client-logo-${i}` : undefined}
                  >
                    {inner}
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="text-center text-[#6C757D] text-sm italic">
            Client logos will appear here once configured.
          </div>
        )}
      </div>

      {/* CSS-only keyframes + hover-pause, co-located with the component */}
      <style>{`
        @keyframes bmp-marquee {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .group:hover .marquee-track { animation-play-state: paused !important; }
        @media (prefers-reduced-motion: reduce) {
          .marquee-track { animation: none !important; }
        }
      `}</style>
    </section>
  );
};

export default ClientsSectionML;
